import { cn } from "@/lib/utils";
import logoHeader320 from "@/assets/optimized/vars-logo-header-320.webp";
import logoHeader640 from "@/assets/optimized/vars-logo-header-640.webp";
import logoSq from "@/assets/vars-logo-sq.png";
import logoWhite320 from "@/assets/optimized/vars-logo-white-320.webp";
import logoWhite640 from "@/assets/optimized/vars-logo-white-640.webp";
import logoNav from "@/assets/vars-logo-nav.png";
import { useI18n } from "@/context/I18nContext";
import { localizedCompanyName } from "@/lib/utils/seo";

interface VarsLogoProps {
  className?: string;
  variant?: "header" | "white" | "sq" | "nav";
}

/**
 * Renders the original VARS Aquaculture logo images copied from the source assets.
 */
export function VarsLogo({ className, variant = "header" }: VarsLogoProps) {
  const { lang } = useI18n();
  const src =
    variant === "white"
      ? logoWhite320
      : variant === "sq"
        ? logoSq
        : variant === "nav"
          ? logoNav
          : logoHeader320;
  const srcSet =
    variant === "white"
      ? `${logoWhite320} 320w, ${logoWhite640} 640w`
      : variant === "header"
        ? `${logoHeader320} 320w, ${logoHeader640} 640w`
        : undefined;
  const dimensions =
    variant === "white"
      ? { width: 320, height: 108 }
      : variant === "sq"
        ? { width: 341, height: 341 }
        : variant === "header"
          ? { width: 320, height: 110 }
          : { width: 991, height: 341 };

  return (
    <div className={cn("flex items-center select-none", className)}>
      <img
        src={src}
        srcSet={srcSet}
        sizes={srcSet ? "(min-width: 1024px) 144px, 120px" : undefined}
        alt={localizedCompanyName(lang)}
        width={dimensions.width}
        height={dimensions.height}
        loading={variant === "white" ? "lazy" : undefined}
        decoding="async"
        className={cn(
          "w-auto object-contain transition-transform duration-300 hover:scale-[1.02]",
          // h-9 rather than h-10 at the smallest widths so the wordmark clears
          // the menu button and action icons on a 320 px screen; everything
          // from sm up is unchanged.
          variant === "sq" ? "h-10 w-10" : "h-9 sm:h-10 lg:h-12",
        )}
      />
    </div>
  );
}
